import * as React from 'react';
import { cn } from '../lib/utils';
import { Skeleton } from './ui/skeleton';

export interface StatCardProps {
  label: React.ReactNode;
  /** Pre-formatted. Money goes through formatMoney *before* it reaches here. */
  value: React.ReactNode;
  /** Sits opposite the label — a marker, not a focal point. */
  icon?: React.ReactNode;
  /**
   * Movement against the previous period. `direction` drives the colour;
   * `label` is the whole caption ("+20.1% from last month"), because the
   * comparison window varies per tile and only the caller knows it.
   */
  delta?: {
    direction: 'up' | 'down' | 'flat';
    label: React.ReactNode;
  };
  loading?: boolean;
  className?: string;
}

/** A single metric tile for the top row of a dashboard. */
export function StatCard({ label, value, icon, delta, loading = false, className }: StatCardProps) {
  return (
    <div
      className={cn(
        'flex h-full flex-col gap-2 rounded-xl border bg-card p-4 shadow-xs dark:shadow-none',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-muted-foreground">{label}</span>
        {icon ? <span className="text-muted-foreground [&_svg]:size-4">{icon}</span> : null}
      </div>

      {loading ? (
        <Skeleton className="mt-1 h-7 w-2/3" />
      ) : (
        <div className="text-2xl font-semibold tracking-tight tabular-nums">{value}</div>
      )}

      {delta ? (
        <span
          className={cn(
            'text-xs',
            delta.direction === 'up' && 'text-success',
            delta.direction === 'down' && 'text-destructive',
            delta.direction === 'flat' && 'text-muted-foreground',
          )}
        >
          {delta.label}
        </span>
      ) : null}
    </div>
  );
}
